import { z } from 'zod';

/**
 * Feature flag key validation
 * Must be lowercase with hyphens (slug format)
 */
const featureFlagKeySchema = z
  .string()
  .min(3, 'Key must be at least 3 characters')
  .max(50, 'Key must be at most 50 characters')
  .regex(
    /^[a-z][a-z0-9-]*[a-z0-9]$/,
    'Key must be lowercase with hyphens (e.g., "new-feature", "booking-v2")'
  );

/**
 * Rule condition type enum
 */
const RuleConditionTypeSchema = z.enum(['role', 'user', 'platform', 'geography']);

/**
 * Rule condition operator enum
 */
const RuleConditionOperatorSchema = z.enum(['in', 'not_in', 'equals']);

/**
 * Individual targeting condition schema
 */
export const RuleConditionSchema = z.object({
  type: RuleConditionTypeSchema,
  operator: RuleConditionOperatorSchema,
  values: z.array(z.string()).min(1, 'At least one value is required'),
});

export type RuleCondition = z.infer<typeof RuleConditionSchema>;

/**
 * Targeting rule operator for combining conditions
 */
const TargetingRuleOperatorSchema = z.enum(['AND', 'OR']);

/**
 * Complete targeting rule schema
 */
export const TargetingRuleSchema = z.object({
  id: z.string().uuid('Rule ID must be a valid UUID'),
  name: z
    .string()
    .min(2, 'Rule name must be at least 2 characters')
    .max(100, 'Rule name must be at most 100 characters'),
  conditions: z.array(RuleConditionSchema).min(1, 'At least one condition is required'),
  operator: TargetingRuleOperatorSchema,
  enabled: z.boolean(),
});

export type TargetingRule = z.infer<typeof TargetingRuleSchema>;

/**
 * Kill switch metadata schema
 */
export const KillSwitchSchema = z.object({
  triggeredAt: z.string().datetime('Triggered at must be a valid ISO datetime'),
  triggeredBy: z.string().min(1, 'Triggered by user ID is required'),
  reason: z
    .string()
    .min(10, 'Reason must be at least 10 characters')
    .max(500, 'Reason must be at most 500 characters'),
});

export type KillSwitch = z.infer<typeof KillSwitchSchema>;

/**
 * Feature flag configuration schema
 */
export const FeatureFlagConfigSchema = z.object({
  rolloutPercentage: z.number().min(0).max(100).default(100),
  targetingRules: z.array(TargetingRuleSchema).default([]),
  dependencies: z.array(z.string()).default([]),
  killSwitch: KillSwitchSchema.nullable().optional(),
  lastModifiedBy: z.string().optional(),
  lastModifiedAt: z.string().datetime().optional(),
});

export type FeatureFlagConfig = z.infer<typeof FeatureFlagConfigSchema>;

/**
 * Create feature flag schema
 */
export const CreateFeatureFlagSchema = z.object({
  key: featureFlagKeySchema,
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be at most 100 characters'),
  description: z.string().max(500, 'Description must be at most 500 characters').optional(),
  enabled: z.boolean().default(false).optional(),
  config: FeatureFlagConfigSchema.optional(),
});

export type CreateFeatureFlagRequest = z.infer<typeof CreateFeatureFlagSchema>;

/**
 * Update feature flag schema
 */
export const UpdateFeatureFlagSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  description: z.string().max(500).optional(),
  enabled: z.boolean().optional(),
  config: FeatureFlagConfigSchema.partial().optional(),
});

export type UpdateFeatureFlagRequest = z.infer<typeof UpdateFeatureFlagSchema>;

/**
 * Feature flag check response schema
 */
export const FeatureFlagCheckSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
});

export type FeatureFlagCheckResponse = z.infer<typeof FeatureFlagCheckSchema>;

/**
 * Response type for feature flag
 */
export interface FeatureFlagResponse {
  id: string;
  key: string;
  name: string;
  description: string | null;
  enabled: boolean;
  config: FeatureFlagConfig | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
